<template>
  <div class="osk-filters-sizes-info">
    <div class="osk-filters-sizes-info__title">
      {{ $t('components.business.products.filters.sizes.sizesInfo.title') }}
    </div>

    <div class="osk-filters-sizes-info__subtitle">
      {{ $t('components.business.products.filters.sizes.sizesInfo.subtitle') }}
    </div>

    <div class="osk-filters-sizes-info__top">
      <div
        v-if="categorySize"
        class="osk-filters-sizes-info__size-name"
      >
        {{ categorySize.sizesValue.name }} - {{ categorySize.sizesValue.code }}
      </div>

      <UikitLabel
        v-if="label"
        :text="label"
        class="osk-filters-sizes-info__label"
        bold
      />
    </div>

    <div class="osk-filters-sizes-info__buttons">
      <div
        v-for="size in categorySize?.sizesValue.values"
        :key="size.id"
        :class="{
          'osk-filters-sizes-info__button': true,
          'osk-filters-sizes-info__button_active': currentSize?.id === size.id,
          'osk-filters-sizes-info__button_isSelected': selected?.find((i) => i.id === size.id),
        }"
        @click="chooseSize(size)"
      >
        {{ size.name }}
      </div>
    </div>

    <div
      class="osk-filters-sizes-info__compared"
    >
      <div class="osk-filters-sizes-info__compared-title">
        {{ $t('components.business.products.filters.sizes.sizesInfo.comparedTitle') }}
      </div>

      <div
        v-for="(chart, index) in currentSize?.charts"
        :key="index"
        class="osk-filters-sizes-info__compared-item"
      >
        <div class="osk-filters-sizes-info__compared-item-name">
          {{ chart.name }} <span>({{ chart.code }})</span>
        </div>

        <div class="osk-filters-sizes-info__compared-item-size">
          {{ chart.value }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash.clonedeep';

import {
  CategorySize, SizeValue, ListValue,
} from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const props = defineProps<{
  categorySize?: CategorySize
  selectedItems?: ListValue[]
  isCatalog?: boolean
  page?: string
}>();

const emits = defineEmits<{
  (e: 'update:selectedItems', value?: ListValue[]): void,
}>();

const currentSize = ref<SizeValue>();

const selected = ref(genSelected());

const label = computed(() => selected.value?.length);

function genSelected() {
  return cloneDeep(props.selectedItems);
}

function chooseSize(size: SizeValue) {
  currentSize.value = size;

  const selectedIndex = selected.value?.findIndex((i) => i.id === size.id);

  if (selectedIndex === -1) {
    selected.value?.push({
      id: size.id,
      value: size.name,
    });
  } else if (typeof selectedIndex === 'number') {
    selected.value?.splice(selectedIndex, 1);
  }

  emits('update:selectedItems', selected.value);

  if (props.isCatalog) {
    $addEvent(AnalyticsEvents.FiltersLinkClick, {
      chapter: 'size',
      // eslint-disable-next-line no-param-reassign
      id_chapter: (selected.value?.map((val) => val.id) ?? []).reduce((result, id) => result += `${id};`, '').slice(0, -1),
      id_item: String(size.id),
      page: props.page,
    });
  }
}

watch(() => props.selectedItems, () => {
  selected.value = genSelected();
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-filters-sizes-info {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: 18px;

    @include media-query(md-and-up) {
      margin-bottom: $indent-compact;
    }
  }

  &__subtitle {
    @include font-style($font-size-caption, $line-height-caption, $font-weight-medium);
    margin-bottom: 49px;
    color: $grey;

    @include media-query(md-and-up) {
      margin-bottom: 42px;
    }
  }

  &__top {
    display: flex;
    align-items: center;
    margin-bottom: 26px;
  }

  &__label {
    margin-left: auto;
  }

  &__size-name {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    font-weight: $font-weight-bold;

    @include media-query(md-and-up) {
      @include font-style($font-size-body, $line-height-body, $font-weight-bold);
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 34px;

    @include media-query(md-and-up) {
      margin-right: 0;
      margin-bottom: 42px;
    }
  }

  &__button {
    width: 62px;
    height: 52px;
    border: 0.5px solid $grey-2;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    @include media-query(md-and-up) {
      width: 52px;
    }

    &_isSelected {
      border-color: $black;
    }

    &_active {
      background: $black;
      color: $white;
    }
  }

  &__compared {
    width: 100%;
  }

  &__compared-title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: 18px;

    @include media-query(md-and-up) {
      @include font-style($font-size-body, $line-height-body, $font-weight-bold);
      margin-bottom: 10px;
    }
  }

  &__compared-item {
    padding: $indent-medium 0;
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 0.5px solid $grey-3;
    }
  }
}
</style>
